import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  CreditCard, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Link as LinkIcon,
  Ban,
  RefreshCw,
  DollarSign,
  TrendingUp,
  Clock,
  History
} from 'lucide-react';
import { format } from 'date-fns';
import { parseDateSafe } from '@/lib/timezoneUtils';
import { ptBR } from 'date-fns/locale';
import {
  useAutoChargePayments,
  useChargePaymentNow,
  useDisableAutoCharge,
  useCreatePaymentLink,
  type AutoChargePeriod,
  type AutoChargeStatusFilter,
  type AutoChargePayment,
  type AutoChargeSummary,
  type ChargeResult
} from '@/hooks/finance/useAutoChargePayments';
import { ChargePaymentModal } from './ChargePaymentModal';
import { ChargeAttemptsHistory } from './ChargeAttemptsHistory';

const PERIOD_OPTIONS: { value: AutoChargePeriod; label: string }[] = [
  { value: 'today', label: 'Hoje' },
  { value: 'this_week', label: 'Esta Semana' },
  { value: 'this_month', label: 'Este Mês' },
  { value: 'all', label: 'Todos' },
];

const STATUS_OPTIONS: { value: AutoChargeStatusFilter; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'success', label: 'Sucesso' },
  { value: 'retry_pending', label: 'Retry Pendente' },
  { value: 'exhausted', label: 'Esgotou Retries' },
  { value: 'no_card', label: 'Sem Cartão' },
];

function formatCurrency(value: number, currency: string = 'USD') {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(value);
}

function getStatusBadge(payment: AutoChargePayment) {
  const status = payment.auto_charge_status;
  const attempts = payment.auto_charge_attempts || 0;

  if (payment.status === 'paid') {
    return <Badge className="bg-green-500 text-white">Pago</Badge>;
  }

  if (status === 'charged') {
    return <Badge className="bg-green-500 text-white">Cobrado</Badge>;
  }

  if (status === 'no_card') {
    return <Badge variant="outline" className="border-orange-500 text-orange-500">Sem Cartão</Badge>;
  }

  if (status === 'failed' && attempts >= 3) {
    return <Badge variant="destructive">Esgotou Retries</Badge>;
  }

  if (status === 'failed') {
    return <Badge variant="destructive">Falhou ({attempts}/3)</Badge>;
  }

  if (status === 'processing') {
    return <Badge variant="secondary">Processando</Badge>;
  }

  return <Badge variant="outline">Pendente</Badge>;
}

interface SummaryCardProps {
  title: string;
  icon: React.ReactNode;
  count: number;
  amount: number;
  totalAmount: number;
  countColor?: string;
}

function SummaryCard({ title, icon, count, amount, totalAmount, countColor }: SummaryCardProps) {
  const percentage = totalAmount > 0 ? (amount / totalAmount) * 100 : 0;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent className="space-y-1">
        <div className={`text-2xl font-bold ${countColor || ''}`}>
          {count} <span className="text-sm font-normal text-muted-foreground">parcela{count !== 1 ? 's' : ''}</span>
        </div>
        <p className="text-sm font-medium">
          {formatCurrency(amount)}
        </p>
        <p className="text-xs text-muted-foreground">
          {percentage.toFixed(1)}% do total
        </p>
      </CardContent>
    </Card>
  );
}

export function AutoChargeTab() {
  const [period, setPeriod] = useState<AutoChargePeriod>('today');
  const [statusFilter, setStatusFilter] = useState<AutoChargeStatusFilter>('all');
  const [confirmAction, setConfirmAction] = useState<{ type: 'disable' | 'link'; payment: AutoChargePayment } | null>(null);
  
  // New modal states
  const [chargeModalPayment, setChargeModalPayment] = useState<AutoChargePayment | null>(null);
  const [historyPaymentId, setHistoryPaymentId] = useState<string | null>(null);
  const [historyPaymentDesc, setHistoryPaymentDesc] = useState<string>('');

  const { data, isLoading, refetch } = useAutoChargePayments({ period, statusFilter });
  const chargeNow = useChargePaymentNow();
  const disableAutoCharge = useDisableAutoCharge();
  const createPaymentLink = useCreatePaymentLink();

  const payments = data?.payments || [];
  const summary = data?.summary;

  const handleCharge = async (paymentId: string): Promise<ChargeResult> => {
    return await chargeNow.mutateAsync(paymentId);
  };

  const handleConfirmAction = () => {
    if (!confirmAction) return;

    const { type, payment } = confirmAction;

    switch (type) {
      case 'disable':
        disableAutoCharge.mutate(payment.id);
        break;
      case 'link':
        createPaymentLink.mutate({
          contactId: payment.deal.contact_id,
          paymentId: payment.id,
          amount: payment.amount - (payment.paid_amount || 0)
        });
        break;
    }

    setConfirmAction(null);
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Cobranças Hoje</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary?.total_today || 0}</div>
            <p className="text-xs text-muted-foreground">
              {formatCurrency(summary?.total_amount_today || 0)}
            </p>
          </CardContent>
        </Card>

        <SummaryCard
          title="Sucesso"
          icon={<CheckCircle className="h-4 w-4 text-green-500" />}
          count={summary?.success_count || 0}
          amount={summary?.success_amount || 0}
          totalAmount={summary?.total_amount_today || 0}
          countColor="text-green-600"
        />

        <SummaryCard
          title="Retry Pendente"
          icon={<RefreshCw className="h-4 w-4 text-amber-500" />}
          count={summary?.failed_retry_pending || 0}
          amount={summary?.failed_retry_pending_amount || 0}
          totalAmount={summary?.total_amount_today || 0}
          countColor="text-amber-600"
        />

        <SummaryCard
          title="Esgotou Retries"
          icon={<XCircle className="h-4 w-4 text-destructive" />}
          count={summary?.exhausted_retries || 0}
          amount={summary?.exhausted_retries_amount || 0}
          totalAmount={summary?.total_amount_today || 0}
          countColor="text-destructive"
        />

        <SummaryCard
          title="Sem Cartão"
          icon={<CreditCard className="h-4 w-4 text-muted-foreground" />}
          count={summary?.no_card || 0}
          amount={summary?.no_card_amount || 0}
          totalAmount={summary?.total_amount_today || 0}
        />
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Período:</span>
              <Select value={period} onValueChange={(v) => setPeriod(v as AutoChargePeriod)}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERIOD_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Status:</span>
              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as AutoChargeStatusFilter)}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Atualizar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Payments Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Cobranças Automáticas
            <Badge variant="outline">{payments.length} registros</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : payments.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhuma cobrança automática encontrada.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Deal</TableHead>
                    <TableHead>Parcela</TableHead>
                    <TableHead>Valor</TableHead>
                    <TableHead>Vencimento</TableHead>
                    <TableHead>Tentativas</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Último Erro</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map((payment) => {
                    const contact = payment.deal.contact;
                    const clientName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'N/A';
                    const hasCard = !!contact.stripe_default_payment_method_id;
                    const canCharge = hasCard && payment.status !== 'paid' && payment.auto_charge_status !== 'charged';

                    return (
                      <TableRow key={payment.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{clientName}</span>
                            {hasCard ? (
                              <CreditCard className="h-4 w-4 text-green-500" />
                            ) : (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <AlertTriangle className="h-4 w-4 text-orange-500" />
                                  </TooltipTrigger>
                                  <TooltipContent>Sem cartão cadastrado</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {payment.deal.name}
                          </span>
                        </TableCell>
                        <TableCell>
                          {payment.installment_number ? `#${payment.installment_number}` : '-'}
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">
                            {formatCurrency(payment.amount - (payment.paid_amount || 0))}
                          </span>
                        </TableCell>
                        <TableCell>
                          {parseDateSafe(payment.due_date) 
                            ? format(parseDateSafe(payment.due_date)!, 'dd/MM/yyyy', { locale: ptBR })
                            : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {payment.auto_charge_attempts || 0}/3
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(payment)}
                        </TableCell>
                        <TableCell>
                          {payment.last_charge_error ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <span className="text-xs text-destructive truncate max-w-[150px] block">
                                    {payment.last_charge_error.substring(0, 30)}...
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-[300px]">
                                  {payment.last_charge_error}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            {canCharge && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => setChargeModalPayment(payment)}
                                      disabled={chargeNow.isPending}
                                    >
                                      <DollarSign className="h-4 w-4 text-green-600" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Cobrar Agora</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => {
                                      setHistoryPaymentId(payment.id);
                                      setHistoryPaymentDesc(`${payment.deal.name} - Parcela #${payment.installment_number || '-'}`);
                                    }}
                                  >
                                    <History className="h-4 w-4 text-muted-foreground" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Ver Histórico</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setConfirmAction({ type: 'link', payment })}
                                    disabled={createPaymentLink.isPending}
                                  >
                                    <LinkIcon className="h-4 w-4 text-blue-600" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Gerar Link</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>

                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setConfirmAction({ type: 'disable', payment })}
                                    disabled={disableAutoCharge.isPending}
                                  >
                                    <Ban className="h-4 w-4 text-muted-foreground" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Marcar Manual</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog (for disable/link only) */}
      <AlertDialog open={!!confirmAction} onOpenChange={() => setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.type === 'disable' && 'Desativar Cobrança Automática'}
              {confirmAction?.type === 'link' && 'Gerar Link de Pagamento'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction?.type === 'disable' && 
                'Esta parcela será removida da fila de cobrança automática e precisará ser cobrada manualmente.'
              }
              {confirmAction?.type === 'link' && 
                `Será gerado um link de pagamento de ${formatCurrency(confirmAction.payment.amount - (confirmAction.payment.paid_amount || 0))} para o cliente.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmAction}>
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Charge Payment Modal */}
      <ChargePaymentModal
        open={!!chargeModalPayment}
        onOpenChange={(open) => !open && setChargeModalPayment(null)}
        payment={chargeModalPayment}
        onCharge={handleCharge}
        isCharging={chargeNow.isPending}
      />

      {/* Charge Attempts History Modal */}
      <ChargeAttemptsHistory
        open={!!historyPaymentId}
        onOpenChange={(open) => !open && setHistoryPaymentId(null)}
        paymentId={historyPaymentId}
        paymentDescription={historyPaymentDesc}
      />
    </div>
  );
}
